import { Directive, DoCheck, ElementRef, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatPaginator } from '@angular/material/paginator';

// Adds numbered page buttons (1 2 … 7 8 9 … 20) to every mat-paginator it's
// imported alongside, between the previous and next arrows - so a page can
// be jumped to directly. Styled globally in styles.css (.app-pager-*).
@Directive({
  selector: 'mat-paginator',
  standalone: true,
})
export class PaginatorPagesDirective implements DoCheck, OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly paginator = inject(MatPaginator, { self: true });
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private container: HTMLElement | null = null;
  private lastKey = '';

  // Re-draw only when the page, page size or total actually changes.
  ngDoCheck(): void {
    if (!this.isBrowser) return;
    const key = `${this.paginator.pageIndex}|${this.paginator.pageSize}|${this.paginator.length}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    // After Angular has rendered the paginator's own buttons.
    queueMicrotask(() => this.render());
  }

  ngOnDestroy(): void {
    this.container?.remove();
  }

  private render(): void {
    const actions: HTMLElement | null = this.host.nativeElement.querySelector('.mat-mdc-paginator-range-actions');
    const next = actions?.querySelector('.mat-mdc-paginator-navigation-next');
    if (!actions || !next) return;
    if (!this.container || !actions.contains(this.container)) {
      this.container = document.createElement('div');
      this.container.className = 'app-pager-pages';
      actions.insertBefore(this.container, next);
    }
    this.container.replaceChildren();

    const total = this.paginator.getNumberOfPages();
    const current = this.paginator.pageIndex;
    this.container.hidden = total <= 1;
    if (total <= 1) return;

    for (const page of this.pageWindow(current, total)) {
      if (page === null) {
        const gap = document.createElement('span');
        gap.className = 'app-pager-gap';
        gap.textContent = '…';
        this.container.appendChild(gap);
        continue;
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'app-pager-page' + (page === current ? ' is-current' : '');
      button.textContent = String(page + 1);
      button.setAttribute('aria-label', `Page ${page + 1}`);
      if (page === current) button.setAttribute('aria-current', 'page');
      button.addEventListener('click', () => this.goTo(page));
      this.container.appendChild(button);
    }
  }

  // First, last, and the pages around the current one, with gaps (null)
  // between - at most 7 slots, so it never crowds the bar.
  private pageWindow(current: number, total: number): (number | null)[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const pages = new Set([0, total - 1, current - 1, current, current + 1]);
    if (current <= 2) [1, 2, 3].forEach((p) => pages.add(p));
    if (current >= total - 3) [total - 4, total - 3, total - 2].forEach((p) => pages.add(p));
    const sorted = [...pages].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
    const out: (number | null)[] = [];
    sorted.forEach((p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) out.push(null);
      out.push(p);
    });
    return out;
  }

  private goTo(page: number): void {
    if (page === this.paginator.pageIndex || this.paginator.disabled) return;
    const previousPageIndex = this.paginator.pageIndex;
    this.paginator.pageIndex = page;
    // Same event the arrows send, so every page's (page) handler just works.
    this.paginator.page.emit({
      pageIndex: page,
      previousPageIndex,
      pageSize: this.paginator.pageSize,
      length: this.paginator.length,
    });
  }
}
